import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { AnalyticsFlagImpactDto } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';

interface FlagImpactChartProps {
  impact: AnalyticsFlagImpactDto | undefined;
  currency: string;
}

function readableFlag(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

export function FlagImpactChart({ currency, impact }: FlagImpactChartProps) {
  const data = (impact?.byFlagType ?? []).map((flag) => ({
    flagType: readableFlag(flag.flagType),
    avgPnl: flag.avgPnl,
    tradeCount: flag.tradeCount,
  }));

  return (
    <section className="rounded border border-border bg-surface p-4">
      <h2 className="text-lg font-semibold">Flag Impact</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded border border-danger/40 bg-danger/10 p-3">
          <div className="text-sm text-text-muted">Trades WITH behavioural flags</div>
          <div className="mt-1 text-xl font-semibold tabular-nums">
            avg {formatCurrency(impact?.withFlags.avgPnl, currency)}
          </div>
          <div className="mt-1 text-sm text-text-muted">
            {impact?.withFlags.tradeCount ?? 0} closed trades
          </div>
        </div>
        <div className="rounded border border-success/40 bg-success/10 p-3">
          <div className="text-sm text-text-muted">Trades WITHOUT flags</div>
          <div className="mt-1 text-xl font-semibold tabular-nums">
            avg {formatCurrency(impact?.withoutFlags.avgPnl, currency)}
          </div>
          <div className="mt-1 text-sm text-text-muted">
            {impact?.withoutFlags.tradeCount ?? 0} closed trades
          </div>
        </div>
      </div>
      {data.length === 0 ? (
        <p className="mt-4 text-sm text-text-muted">
          No closed trades have matched behavioural flags.
        </p>
      ) : (
        <div className="mt-4 h-72" role="img" aria-label="Average P&L by behavioural flag type.">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 16, right: 24 }}>
              <CartesianGrid stroke="#2A3142" horizontal={false} />
              <XAxis
                type="number"
                stroke="#A1A8B8"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => formatCurrency(Number(value), currency)}
              />
              <YAxis
                type="category"
                dataKey="flagType"
                stroke="#A1A8B8"
                tick={{ fontSize: 12 }}
                width={140}
              />
              <Tooltip
                contentStyle={{
                  background: '#222837',
                  border: '1px solid #2A3142',
                  color: '#E6E9EF',
                }}
                formatter={(value, _name, item) => [
                  `${formatCurrency(Number(value), currency)} avg across ${
                    item.payload.tradeCount
                  } trades`,
                  'Avg P&L',
                ]}
              />
              <Bar dataKey="avgPnl" name="Avg P&L" fill="#60A5FA" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
